import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FcGoogle } from 'react-icons/fc';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useUserContext } from '../context/userContext';
import { useColorContext } from '../context/colorContext';
import { useLanguageContext } from '../context/languageContext';
import { isSupabaseConfigured, supabase } from '../services/supabaseClient';
import BrandLogo from '../components/ui/BrandLogo';

const resolveBackendBaseUrl = () => {
	const rawUrl = String(process.env.REACT_APP_BACKEND_URL || '').trim();
	if (!rawUrl) return 'http://localhost:3001';

	if (/^https?:\/\//i.test(rawUrl)) {
		return rawUrl.replace(/\/+$/, '');
	}

	if (/^:\d+$/.test(rawUrl)) {
		return `http://localhost${rawUrl}`;
	}

	if (/^(localhost|127\.0\.0\.1):\d+$/i.test(rawUrl)) {
		return `http://${rawUrl}`;
	}

	return rawUrl.replace(/\/+$/, '');
};

const BACKEND_BASE_URL = resolveBackendBaseUrl();
const RECOVERY_SESSION_LOCK_KEY = 'plynote_recovery_session_lock';

const buildErrorMessage = (payload, status, fallback) => String(
	payload?.error || payload?.message || `${fallback} (${status})`
);

const setRecoverySessionLock = (locked) => {
	if (typeof window === 'undefined') {
		return;
	}

	try {
		if (locked) {
			window.sessionStorage.setItem(RECOVERY_SESSION_LOCK_KEY, '1');
			return;
		}
		window.sessionStorage.removeItem(RECOVERY_SESSION_LOCK_KEY);
	} catch {
		// Ignore storage errors.
	}
};

const normalizeUserProfile = (user = {}) => ({
	user_id: user.id ?? user.userId ?? null,
	name: user.name ?? '',
	first_name: user.firstName ?? user.first_name ?? '',
	last_name: user.lastName ?? user.last_name ?? '',
	username: user.username ?? '',
	email: user.email ?? '',
	phone_number: user.phoneNumber ?? user.phone_number ?? '',
	role: user.role ?? '',
	avatar_url: user.avatarUrl ?? user.avatar_url ?? '',
	created_at: user.createdAt ?? user.created_at ?? null,
	updated_at: user.updatedAt ?? user.updated_at ?? null,
});

function Spinner({ size = 16, color = '#ffffff' }) {
	return (
		<span style={{
			display: 'inline-block',
			width: size,
			height: size,
			border: `2px solid ${color}44`,
			borderTopColor: color,
			borderRadius: '50%',
			animation: 'ply-spin 0.65s linear infinite',
			flexShrink: 0,
		}} />
	);
}

function AnimatedButton({
	onClick,
	disabled,
	type = 'button',
	children,
	baseStyle,
	hoverStyle,
	activeStyle,
	className,
}) {
	const [isHovered, setIsHovered] = useState(false);
	const [isPressed, setIsPressed] = useState(false);

	return (
		<button
			type={type}
			onClick={onClick}
			disabled={disabled}
			className={className}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => {
				setIsHovered(false);
				setIsPressed(false);
			}}
			onMouseDown={() => setIsPressed(true)}
			onMouseUp={() => setIsPressed(false)}
			style={{
				...baseStyle,
				...(isHovered ? hoverStyle : null),
				...(isPressed ? activeStyle : null),
			}}
		>
			{children}
		</button>
	);
}

function AnimatedInput({ type, value, onChange, placeholder, autoComplete, baseStyle, focusStyle, maxLength, rightSlot }) {
	const [isFocused, setIsFocused] = useState(false);

	const mergedStyle = {
		...baseStyle,
		...(isFocused ? focusStyle : {}),
		outline: 'none',
	};

	if (rightSlot) {
		return (
			<div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
				<input
					type={type}
					value={value}
					onChange={onChange}
					placeholder={placeholder}
					autoComplete={autoComplete}
					maxLength={maxLength}
					onFocus={() => setIsFocused(true)}
					onBlur={() => setIsFocused(false)}
					style={{ ...mergedStyle, flex: 1, paddingRight: '46px', boxSizing: 'border-box' }}
				/>
				{rightSlot}
			</div>
		);
	}

	return (
		<input
			type={type}
			value={value}
			onChange={onChange}
			placeholder={placeholder}
			autoComplete={autoComplete}
			maxLength={maxLength}
			onFocus={() => setIsFocused(true)}
			onBlur={() => setIsFocused(false)}
			style={mergedStyle}
		/>
	);
}

export default function ConnectionPage() {
	const navigate = useNavigate();
	const {
		bgPrimary,
		bgSecondary,
		textPrimary,
		textSecondary,
		activeAccent,
		theme_type,
	} = useColorContext();
	const {
		setUserProfile,
		setConnectionState,
	} = useUserContext();
	const { t } = useLanguageContext();

	const isLightTheme = String(theme_type || '').toLowerCase() === 'clear';
	const successTextColor = isLightTheme ? '#000000' : '#86EFAC';

	const [mode, setMode] = useState('login');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [formError, setFormError] = useState('');
	const [showLoginPassword, setShowLoginPassword] = useState(false);
	const [showSignupPassword, setShowSignupPassword] = useState(false);
	const [showSignupConfirmPassword, setShowSignupConfirmPassword] = useState(false);
	const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
	const [otpCode, setOtpCode] = useState('');
	const [signupPendingEmail, setSignupPendingEmail] = useState('');
	const [signupPendingData, setSignupPendingData] = useState(null);
	const [otpResendCooldownSec, setOtpResendCooldownSec] = useState(0);
	const [loginForm, setLoginForm] = useState({
		email: '',
		password: '',
	});
	const [signupForm, setSignupForm] = useState({
		name: '',
		username: '',
		email: '',
		password: '',
		confirmPassword: '',
	});
	const [passwordFlow, setPasswordFlow] = useState('none');
	const [passwordResetEmail, setPasswordResetEmail] = useState('');
	const [passwordResetSent, setPasswordResetSent] = useState(false);
	const [passwordResetForm, setPasswordResetForm] = useState({
		password: '',
		confirmPassword: '',
	});
	const location = useLocation();

	const hexToRgba = (hex, alpha) => {
		const safeHex = /^#([A-Fa-f0-9]{6})$/.test(hex || '') ? hex : '#FFFFFF';
		const r = parseInt(safeHex.slice(1, 3), 16);
		const g = parseInt(safeHex.slice(3, 5), 16);
		const b = parseInt(safeHex.slice(5, 7), 16);
		return `rgba(${r}, ${g}, ${b}, ${alpha})`;
	};

	const darkenHex = (hex, amount = 28) => {
		const safeHex = /^#([A-Fa-f0-9]{6})$/.test(hex || '') ? hex : '#000000';
		const r = Math.max(0, parseInt(safeHex.slice(1, 3), 16) - amount);
		const g = Math.max(0, parseInt(safeHex.slice(3, 5), 16) - amount);
		const b = Math.max(0, parseInt(safeHex.slice(5, 7), 16) - amount);
		return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
	};

	const subtitle = useMemo(() => (
		mode === 'login'
			? t('auth.loginSubtitle')
			: t('auth.signupSubtitle')
	), [mode, t]);

	useEffect(() => {
		if (!isVerifyingOtp || otpResendCooldownSec <= 0) {
			return undefined;
		}

		const timeoutId = window.setTimeout(() => {
			setOtpResendCooldownSec((current) => Math.max(0, current - 1));
		}, 1000);

		return () => window.clearTimeout(timeoutId);
	}, [isVerifyingOtp, otpResendCooldownSec]);

	const onChangeLoginField = (field, value) => {
		setLoginForm((current) => ({
			...current,
			[field]: value,
		}));
	};

	const onChangeSignupField = (field, value) => {
		setSignupForm((current) => ({
			...current,
			[field]: value,
		}));
	};

	const applyAuthResponseToContext = (payloadData, authProvider = 'local') => {
		setRecoverySessionLock(false);
		const user = payloadData?.user || {};
		const session = payloadData?.session || {};

		// Register tokens with the browser Supabase client so getSession() works on next refresh
		// and autoRefreshToken can keep the session alive while the user is online.
		if (isSupabaseConfigured && supabase && session.accessToken && session.refreshToken) {
			supabase.auth.setSession({
				access_token: session.accessToken,
				refresh_token: session.refreshToken,
			}).catch(() => {});
		}

		setUserProfile(normalizeUserProfile(user));
		setConnectionState({
			is_authenticated: true,
			is_loading: false,
			connection_status: 'connected',
			connection_error: '',
			auth_provider: authProvider,
			session_expires_at: session.sessionExpiresAt || null,
			access_token: session.accessToken || '',
			refresh_token: session.refreshToken || '',
		});
	};

	const syncGoogleUser = async (googleUser, session) => {
		const response = await fetch(`${BACKEND_BASE_URL}/api/auth/google/sync`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				email: googleUser?.email,
				name: googleUser?.user_metadata?.full_name || googleUser?.user_metadata?.name || '',
				avatarUrl: googleUser?.user_metadata?.avatar_url || '',
				providerUserId: googleUser?.id || '',
				accessToken: session?.access_token || '',
				refreshToken: session?.refresh_token || '',
				sessionExpiresAt: session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : null,
			}),
		});

		const payload = await response.json().catch(() => ({}));
		if (!response.ok || !payload?.success) {
			throw new Error(buildErrorMessage(payload, response.status, 'Google sign-in failed'));
		}

		return payload;
	};

	useEffect(() => {
		if (!isSupabaseConfigured || !supabase) return;

		// Detect PKCE flow (?code=) or implicit flow (#access_token= with type != recovery).
		// Clearing URL tokens synchronously before any await is the StrictMode guard:
		// the second invocation finds nothing and returns early.
		const searchParams = new URLSearchParams(window.location.search);
		const code = searchParams.get('code');

		const hashParams = new URLSearchParams(String(window.location.hash || '').replace(/^#/, ''));
		const hashAccessToken = hashParams.get('access_token');
		const hashRefreshToken = hashParams.get('refresh_token');
		const hashType = hashParams.get('type');
		const isImplicitOAuth = Boolean(hashAccessToken && hashRefreshToken && hashType !== 'recovery');

		if (!code && !isImplicitOAuth) return;

		if (code) {
			searchParams.delete('code');
			const newSearch = searchParams.toString();
			window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));
		} else {
			window.history.replaceState({}, '', window.location.pathname + (window.location.search || ''));
		}

		let isMounted = true;

		const processGoogleCallback = async () => {
			try {
				let session = null;
				if (isImplicitOAuth) {
					const { data: implicitData, error: implicitError } = await supabase.auth.setSession({
						access_token: hashAccessToken,
						refresh_token: hashRefreshToken,
					});
					if (implicitError) throw implicitError;
					session = implicitData?.session;
				} else {
					const { data, error } = await supabase.auth.getSession();
					if (error) throw error;
					session = data?.session;
				}

				const sessionUser = session?.user;
				const provider = sessionUser?.app_metadata?.provider;
				if (!sessionUser || provider !== 'google') return;

				setConnectionState({ is_loading: true, connection_error: '' });
				const payload = await syncGoogleUser(sessionUser, session);

				// Apply to global context regardless of mount state — the component may have
				// unmounted already if Supabase's detectSessionInUrl fired SIGNED_IN first and
				// the /connection route redirected, but the context update must still happen.
				applyAuthResponseToContext(payload.data, 'google');

				if (!isMounted) return;
				setFormError('');
				let googlePendingPlan = '';
				try { googlePendingPlan = sessionStorage.getItem('plynote_pending_plan') || ''; sessionStorage.removeItem('plynote_pending_plan'); } catch { /* ignore */ }
				let googlePendingFrom = null;
				try { const raw = sessionStorage.getItem('plynote_pending_from'); sessionStorage.removeItem('plynote_pending_from'); if (raw) googlePendingFrom = JSON.parse(raw); } catch { /* ignore */ }
				const googleDest = googlePendingPlan && googlePendingPlan !== 'free'
					? `/settings?plan=${encodeURIComponent(googlePendingPlan)}`
					: (googlePendingFrom?.pathname && googlePendingFrom.pathname !== '/connection' ? `${googlePendingFrom.pathname}${googlePendingFrom.search || ''}${googlePendingFrom.hash || ''}` : '/');
				navigate(googleDest);
			} catch (error) {
				if (!isMounted) return;
				setFormError(error.message || 'Google sign-in failed.');
				setConnectionState({
					is_loading: false,
					connection_status: 'error',
					connection_error: error.message || 'Google sign-in failed.',
				});
			} finally {
				if (isMounted) setConnectionState({ is_loading: false });
			}
		};

		processGoogleCallback();

		return () => { isMounted = false; };
	}, [navigate, setConnectionState]);

	useEffect(() => {
		const searchParams = new URLSearchParams(location.search || '');
		const requestedMode = String(searchParams.get('mode') || '').trim().toLowerCase();
		if (requestedMode === 'signup' || requestedMode === 'login') {
			setMode(requestedMode);
		}

		const hashParams = new URLSearchParams(String(location.hash || '').replace(/^#/, ''));
		const oauthErrorCode = String(searchParams.get('error_code') || hashParams.get('error_code') || '').trim();
		const oauthErrorDescriptionRaw = String(searchParams.get('error_description') || hashParams.get('error_description') || '').trim();
		if (oauthErrorCode || oauthErrorDescriptionRaw) {
			const normalizedDescription = oauthErrorDescriptionRaw
				? decodeURIComponent(oauthErrorDescriptionRaw.replace(/\+/g, ' '))
				: '';
			setFormError(
				normalizedDescription
					? `Google sign-in failed: ${normalizedDescription}`
					: 'Google sign-in failed. Check Google/Supabase OAuth configuration.'
			);

			const cleanedParams = new URLSearchParams(location.search || '');
			cleanedParams.delete('error');
			cleanedParams.delete('error_code');
			cleanedParams.delete('error_description');
			cleanedParams.delete('sb');
			const cleanedQuery = cleanedParams.toString();
			window.history.replaceState({}, '', `/connection${cleanedQuery ? `?${cleanedQuery}` : ''}`);
		}

		const isRecoveryCallback = searchParams.get('mode') === 'reset'
			|| searchParams.get('type') === 'recovery'
			|| searchParams.has('token_hash')
			|| location.hash.includes('type=recovery');

		if (!isRecoveryCallback) {
			return;
		}

		setRecoverySessionLock(true);

		const recoveryAccessToken = hashParams.get('access_token');
		const recoveryRefreshToken = hashParams.get('refresh_token');
		if (recoveryAccessToken && recoveryRefreshToken && isSupabaseConfigured && supabase) {
			supabase.auth.setSession({
				access_token: recoveryAccessToken,
				refresh_token: recoveryRefreshToken,
			}).catch((err) => {
				console.warn('password-reset: failed to set session from URL tokens', err?.message);
			});
		}

		setPasswordFlow('recovery');
		setPasswordResetSent(false);
		setFormError('');
	}, [location]);

	const getPostAuthDestination = () => {
		const params = new URLSearchParams(location.search || '');
		const plan = params.get('plan');
		if (plan && plan !== 'free') return `/settings?plan=${encodeURIComponent(plan)}`;
		const from = location.state?.from;
		if (from?.pathname && from.pathname !== '/connection') {
			return `${from.pathname}${from.search || ''}${from.hash || ''}`;
		}
		return '/';
	};

	const signInWithGoogle = async () => {
		setFormError('');
		if (!isSupabaseConfigured || !supabase) {
			setFormError('Supabase is not configured for Google OAuth.');
			return;
		}

		const params = new URLSearchParams(location.search || '');
		const pendingPlan = params.get('plan');
		if (pendingPlan && pendingPlan !== 'free') {
			try { sessionStorage.setItem('plynote_pending_plan', pendingPlan); } catch { /* ignore */ }
		}
		const fromState = location.state?.from;
		if (fromState?.pathname && fromState.pathname !== '/connection') {
			try { sessionStorage.setItem('plynote_pending_from', JSON.stringify(fromState)); } catch { /* ignore */ }
		}

		setConnectionState({ is_loading: true, connection_error: '' });

		try {
			const redirectTo = `${window.location.origin}/connection`;
			const { error } = await supabase.auth.signInWithOAuth({
				provider: 'google',
				options: {
					redirectTo,
				},
			});

			if (error) {
				throw error;
			}
		} catch (error) {
			setFormError(error.message || 'Google sign-in failed.');
			setConnectionState({
				is_loading: false,
				connection_status: 'error',
				connection_error: error.message || 'Google sign-in failed.',
			});
		}
	};

	const submitLogin = async (event) => {
		event.preventDefault();
		setFormError('');

		if (!loginForm.email || !loginForm.password) {
			setFormError('Email and password are required.');
			return;
		}

		setIsSubmitting(true);
		setConnectionState({ is_loading: true, connection_error: '' });

		try {
			const response = await fetch(`${BACKEND_BASE_URL}/api/auth/login`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					email: loginForm.email,
					password: loginForm.password,
				}),
			});

			const payload = await response.json().catch(() => ({}));
			if (!response.ok || !payload?.success) {
				throw new Error(buildErrorMessage(payload, response.status, 'Login failed'));
			}

			applyAuthResponseToContext(payload.data);
			setLoginForm({ email: loginForm.email, password: '' });
			navigate(getPostAuthDestination());
		} catch (error) {
			setFormError(error.message || 'Login failed.');
			setConnectionState({
				is_loading: false,
				connection_status: 'error',
				connection_error: error.message || 'Login failed.',
			});
		} finally {
			setIsSubmitting(false);
			setConnectionState({ is_loading: false });
		}
	};

	const submitSignup = async (event) => {
		event.preventDefault();
		setFormError('');
		const password = String(signupForm.password || '');

		if (!signupForm.name || !signupForm.email || !password) {
			setFormError('Name, email, and password are required.');
			return;
		}

		if (password.length < 8) {
			setFormError('Password must contain at least 8 characters.');
			return;
		}

		if (!/\d/.test(password)) {
			setFormError('Password must contain at least 1 digit.');
			return;
		}

		if (password !== signupForm.confirmPassword) {
			setFormError('Passwords do not match.');
			return;
		}

		setIsSubmitting(true);
		setConnectionState({ is_loading: true, connection_error: '' });

		try {
			const response = await fetch(`${BACKEND_BASE_URL}/api/auth/signup/init`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					email: signupForm.email,
				}),
			});

			const payload = await response.json().catch(() => ({}));
			if (!response.ok || !payload?.success) {
				throw new Error(buildErrorMessage(payload, response.status, 'Unable to send verification code'));
			}

			const retryAfterSec = Number(payload?.data?.retryAfterSec || 0);
			setOtpResendCooldownSec(retryAfterSec);

			setSignupPendingEmail(signupForm.email);
			setSignupPendingData({
				name: signupForm.name,
				username: signupForm.username,
				email: signupForm.email,
				password: signupForm.password,
			});
			setIsVerifyingOtp(true);
			setFormError('');
		} catch (error) {
			setFormError(error.message || 'Unable to send verification code.');
			setConnectionState({
				is_loading: false,
				connection_status: 'error',
				connection_error: error.message || 'Unable to send verification code.',
			});
		} finally {
			setIsSubmitting(false);
			setConnectionState({ is_loading: false });
		}
	};

	const verifyOtpCode = async (event) => {
		if (event) {
			event.preventDefault();
		}

		setFormError('');
		if (!otpCode || otpCode.length < 6) {
			setFormError('Please enter the 6-digit code.');
			return;
		}

		setIsSubmitting(true);
		setConnectionState({ is_loading: true, connection_error: '' });

		try {
			const verifyResponse = await fetch(`${BACKEND_BASE_URL}/api/auth/signup/verify`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					email: signupPendingEmail,
					otpCode,
				}),
			});

			const verifyPayload = await verifyResponse.json().catch(() => ({}));
			if (!verifyResponse.ok || !verifyPayload?.success || !verifyPayload?.data?.verificationToken) {
				throw new Error(buildErrorMessage(verifyPayload, verifyResponse.status, 'Invalid OTP code'));
			}

			const verificationToken = String(verifyPayload.data.verificationToken || '');

			const response = await fetch(`${BACKEND_BASE_URL}/api/auth/signup/finalize`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					name: signupPendingData?.name || '',
					username: signupPendingData?.username || '',
					email: signupPendingData?.email || signupPendingEmail,
					password: signupPendingData?.password || '',
					verificationToken,
				}),
			});

			const payload = await response.json().catch(() => ({}));
			if (!response.ok || !payload?.success) {
				throw new Error(buildErrorMessage(payload, response.status, 'Account creation failed'));
			}

			applyAuthResponseToContext(payload.data);
			setSignupForm({ name: '', username: '', email: '', password: '', confirmPassword: '' });
			setOtpCode('');
			setSignupPendingEmail('');
			setSignupPendingData(null);
			setOtpResendCooldownSec(0);
			setIsVerifyingOtp(false);
			navigate(getPostAuthDestination());
		} catch (error) {
			setFormError(error.message || 'Invalid OTP code.');
			setConnectionState({
				is_loading: false,
				connection_status: 'error',
				connection_error: error.message || 'Invalid OTP code.',
			});
		} finally {
			setIsSubmitting(false);
			setConnectionState({ is_loading: false });
		}
	};

	const resendOtpCode = async () => {
		if (!signupPendingEmail || otpResendCooldownSec > 0 || isSubmitting) {
			return;
		}

		setFormError('');
		setIsSubmitting(true);

		try {
			const response = await fetch(`${BACKEND_BASE_URL}/api/auth/signup/resend`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					email: signupPendingEmail,
				}),
			});

			const payload = await response.json().catch(() => ({}));
			if (!response.ok || !payload?.success) {
				throw new Error(buildErrorMessage(payload, response.status, 'Unable to resend code'));
			}

			setOtpResendCooldownSec(Number(payload?.data?.retryAfterSec || 0));
		} catch (error) {
			setFormError(error.message || 'Unable to resend code.');
		} finally {
			setIsSubmitting(false);
		}
	};

	const submitPasswordResetRequest = async (event) => {
		event.preventDefault();
		setFormError('');

		const email = String(passwordResetEmail || loginForm.email || '').trim();
		if (!email) {
			setFormError('Email is required.');
			return;
		}

		setIsSubmitting(true);
		setConnectionState({ is_loading: true, connection_error: '' });

		try {
			const response = await fetch(`${BACKEND_BASE_URL}/api/auth/password-reset/request`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ email }),
			});

			const payload = await response.json().catch(() => ({}));
			if (!response.ok) {
				throw new Error(buildErrorMessage(payload, response.status, 'Unable to send reset link'));
			}

			setPasswordResetSent(true);
			setPasswordResetEmail(email);
			setFormError('');
		} catch (error) {
			setFormError(error.message || 'Unable to send reset link.');
			setConnectionState({
				is_loading: false,
				connection_status: 'error',
				connection_error: error.message || 'Unable to send reset link.',
			});
		} finally {
			setIsSubmitting(false);
			setConnectionState({ is_loading: false });
		}
	};

	const submitNewPassword = async (event) => {
		event.preventDefault();
		setFormError('');
		const password = String(passwordResetForm.password || '');

		if (!password || password.length < 8) {
			setFormError('Password must contain at least 8 characters.');
			return;
		}

		if (!/\d/.test(password)) {
			setFormError('Password must contain at least 1 digit.');
			return;
		}

		if (password !== passwordResetForm.confirmPassword) {
			setFormError('Passwords do not match.');
			return;
		}

		if (!isSupabaseConfigured || !supabase) {
			setFormError('Supabase is not configured for password reset.');
			return;
		}

		setIsSubmitting(true);
		setConnectionState({ is_loading: true, connection_error: '' });

		try {
			const { error } = await supabase.auth.updateUser({
				password,
			});

			if (error) {
				throw error;
			}

			const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
			if (sessionError) {
				throw sessionError;
			}

			const session = sessionData?.session;
			if (session?.user) {
				setRecoverySessionLock(false);
				applyAuthResponseToContext({
					user: session.user,
					session: {
						accessToken: session.access_token || '',
						refreshToken: session.refresh_token || '',
						sessionExpiresAt: session.expires_at ? new Date(session.expires_at * 1000).toISOString() : null,
					},
				}, session.user?.app_metadata?.provider || 'email');
			}

			setPasswordResetForm({ password: '', confirmPassword: '' });
			setPasswordFlow('none');
			setPasswordResetSent(false);
			setFormError('');
			navigate('/');
		} catch (error) {
			setFormError(error.message || 'Unable to change password.');
			setConnectionState({
				is_loading: false,
				connection_status: 'error',
				connection_error: error.message || 'Unable to change password.',
			});
		} finally {
			setIsSubmitting(false);
			setConnectionState({ is_loading: false });
		}
	};

	// ─── shared style tokens ──────────────────────────────────────────────────

	const inputBase = {
		border: `1.5px solid ${hexToRgba(textSecondary, 0.18)}`,
		borderRadius: '12px',
		padding: '13px 16px',
		color: textPrimary,
		background: hexToRgba(bgPrimary, 0.45),
		fontSize: '15px',
		width: '100%',
		boxSizing: 'border-box',
		transition: 'border-color 0.22s ease, box-shadow 0.22s ease, background 0.22s ease',
	};

	const inputFocus = {
		borderColor: activeAccent,
		boxShadow: `0 0 0 3.5px ${hexToRgba(activeAccent, 0.14)}`,
		background: hexToRgba(bgPrimary, 0.65),
	};

	const primaryBtn = {
		padding: '14px 16px',
		background: `linear-gradient(270deg, ${activeAccent}, #a855f7, #000000, #a855f7, ${activeAccent})`,
		backgroundSize: '300% 300%',
		border: 'none',
		borderRadius: 12,
		color: '#FFFFFF',
		fontSize: '15px',
		fontWeight: 700,
		cursor: isSubmitting ? 'not-allowed' : 'pointer',
		transition: 'box-shadow 260ms cubic-bezier(0.22, 1, 0.36, 1), filter 220ms cubic-bezier(0.22, 1, 0.36, 1)',
		boxShadow: `0 12px 28px ${hexToRgba(activeAccent, 0.22)}`,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: '9px',
	};

	const primaryBtnHover = isSubmitting ? {} : {
		filter: 'brightness(1.08) saturate(1.05)',
		boxShadow: `0 16px 34px ${hexToRgba(activeAccent, 0.35)}`,
	};

	const primaryBtnActive = {
		filter: 'none',
		boxShadow: `0 12px 28px ${hexToRgba(activeAccent, 0.22)}`,
	};

	const ghostBtn = {
		border: `1.5px solid ${hexToRgba(activeAccent, 0.28)}`,
		background: 'transparent',
		color: textPrimary,
		borderRadius: '12px',
		padding: '13px 16px',
		cursor: isSubmitting ? 'not-allowed' : 'pointer',
		fontSize: '15px',
		fontWeight: 560,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: '8px',
		transition: 'background 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
	};

	const ghostBtnHover = isSubmitting ? {} : {
		background: hexToRgba(activeAccent, 0.08),
		borderColor: hexToRgba(activeAccent, 0.5),
		transform: 'translateY(-1px)',
	};

	const eyeBtn = {
		position: 'absolute',
		right: 12,
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		color: textSecondary,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: 4,
		transition: 'color 0.18s ease',
	};

	// ─── render ───────────────────────────────────────────────────────────────

	return (
		<div style={{
			minHeight: '100vh',
			display: 'grid',
			placeItems: 'center',
			background: bgPrimary,
			padding: '24px',
			position: 'relative',
			overflow: 'hidden',
		}}>
			<style>{`
				@keyframes ply-spin { to { transform: rotate(360deg); } }
				@keyframes ply-float1 {
					0%,100% { transform: translate(0,0) scale(1); }
					50%      { transform: translate(30px,-45px) scale(1.08); }
				}
				@keyframes ply-float2 {
					0%,100% { transform: translate(0,0) scale(1); }
					50%      { transform: translate(-22px,35px) scale(0.92); }
				}
				@keyframes ply-float3 {
					0%,100% { transform: translate(0,0) scale(1); }
					50%      { transform: translate(18px,22px) scale(1.05); }
				}
				@keyframes ply-fadein {
					from { opacity:0; transform:translateY(18px); }
					to   { opacity:1; transform:translateY(0); }
				}
				@keyframes ctaGradient {
					0%   { background-position: 0% 50%; }
					50%  { background-position: 100% 50%; }
					100% { background-position: 0% 50%; }
				}
			`}</style>

			{/* background orbs */}
			<div style={{
				position: 'absolute', top: '-18%', left: '-12%',
				width: '520px', height: '520px', borderRadius: '50%',
				background: `radial-gradient(circle, ${hexToRgba(activeAccent, 0.16)} 0%, transparent 70%)`,
				animation: 'ply-float1 14s ease-in-out infinite',
				pointerEvents: 'none',
			}} />
			<div style={{
				position: 'absolute', bottom: '-22%', right: '-12%',
				width: '640px', height: '640px', borderRadius: '50%',
				background: `radial-gradient(circle, ${hexToRgba(activeAccent, 0.11)} 0%, transparent 70%)`,
				animation: 'ply-float2 18s ease-in-out infinite',
				pointerEvents: 'none',
			}} />
			<div style={{
				position: 'absolute', top: '40%', right: '15%',
				width: '280px', height: '280px', borderRadius: '50%',
				background: `radial-gradient(circle, ${hexToRgba(activeAccent, 0.07)} 0%, transparent 70%)`,
				animation: 'ply-float3 10s ease-in-out infinite',
				pointerEvents: 'none',
			}} />

			{/* card */}
			<div style={{
				width: '100%',
				maxWidth: '460px',
				background: hexToRgba(bgSecondary, 0.82),
				backdropFilter: 'blur(28px)',
				WebkitBackdropFilter: 'blur(28px)',
				borderRadius: '24px',
				border: `1px solid ${hexToRgba(activeAccent, 0.16)}`,
				boxShadow: [
					`0 0 0 1px ${hexToRgba(bgSecondary, 0.45)}`,
					`0 36px 72px -12px rgba(0,0,0,0.35)`,
					`0 12px 28px -6px rgba(0,0,0,0.25)`,
					`inset 0 1px 0 ${hexToRgba('#ffffff', 0.06)}`,
				].join(', '),
				padding: '28px',
				position: 'relative',
				animation: 'ply-fadein 0.5s ease both',
			}}>

				{/* brand */}
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						width: '100%',
						marginBottom: '20px',
					}}
				>
					<BrandLogo size={22} />
				</div>

				{isVerifyingOtp ? (
					<>
						<h1 style={{ margin: '0 0 4px', fontSize: '24px', fontWeight: 700, color: textPrimary, letterSpacing: '-0.5px' }}>
							{t('auth.checkEmail')}
						</h1>
						<p style={{ margin: '0 0 18px', fontSize: '14px', color: textSecondary, lineHeight: 1.5 }}>
							{t('auth.verificationSent')}{' '}
							<span style={{ color: textPrimary, fontWeight: 600 }}>{signupPendingEmail}</span>
						</p>

						<form onSubmit={verifyOtpCode} style={{ display: 'grid', gap: '10px' }}>
							<AnimatedInput
								type="text"
								value={otpCode}
								onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
								placeholder="0  0  0  0  0  0"
								maxLength="6"
								autoComplete="off"
								baseStyle={{
									...inputBase,
									fontSize: '26px',
									letterSpacing: '10px',
									textAlign: 'center',
									fontWeight: 700,
									padding: '16px',
								}}
								focusStyle={inputFocus}
							/>

							<AnimatedButton
								type="submit"
								disabled={isSubmitting || otpCode.length !== 6}
								baseStyle={{
									...primaryBtn,
									opacity: otpCode.length !== 6 || isSubmitting ? 0.65 : 1,
									cursor: otpCode.length !== 6 || isSubmitting ? 'not-allowed' : 'pointer',
								}}
								hoverStyle={otpCode.length === 6 && !isSubmitting ? primaryBtnHover : {}}
								activeStyle={otpCode.length === 6 && !isSubmitting ? primaryBtnActive : {}}
							>
								{isSubmitting ? <><Spinner />{t('auth.verifying')}</> : t('auth.verifyCode')}
							</AnimatedButton>

							<AnimatedButton
								type="button"
								onClick={resendOtpCode}
								disabled={isSubmitting || otpResendCooldownSec > 0}
								baseStyle={{
									...ghostBtn,
									opacity: isSubmitting || otpResendCooldownSec > 0 ? 0.55 : 1,
									cursor: isSubmitting || otpResendCooldownSec > 0 ? 'not-allowed' : 'pointer',
								}}
								hoverStyle={!isSubmitting && otpResendCooldownSec === 0 ? ghostBtnHover : {}}
							>
								{otpResendCooldownSec > 0 ? t('auth.resendIn', { sec: otpResendCooldownSec }) : t('auth.resendCode')}
							</AnimatedButton>

							<AnimatedButton
								type="button"
								onClick={() => {
									setIsVerifyingOtp(false);
									setOtpCode('');
									setSignupPendingEmail('');
									setSignupPendingData(null);
									setOtpResendCooldownSec(0);
									setFormError('');
								}}
								disabled={isSubmitting}
								baseStyle={{ ...ghostBtn, opacity: isSubmitting ? 0.55 : 1 }}
								hoverStyle={ghostBtnHover}
							>
								{t('auth.back')}
							</AnimatedButton>
						</form>

						{formError && (
							<div style={{
								marginTop: '12px',
								padding: '11px 14px',
								borderRadius: '10px',
								background: 'rgba(248,113,113,0.1)',
								border: '1px solid rgba(248,113,113,0.25)',
								color: '#F87171',
								fontSize: '13.5px',
								lineHeight: 1.4,
							}}>
								{formError}
							</div>
						)}
					</>
				) : (
					<>
						<h1 style={{ margin: '0 0 4px', fontSize: '24px', fontWeight: 700, color: textPrimary, letterSpacing: '-0.5px' }}>
							{passwordFlow === 'recovery'
								? t('auth.newPassword')
								: passwordFlow === 'request'
								? t('auth.resetPassword')
								: mode === 'login' ? t('auth.signIn') : t('auth.createAccount')}
						</h1>
						<p style={{ margin: '0 0 18px', fontSize: '14px', color: textSecondary, lineHeight: 1.5 }}>
							{passwordFlow === 'recovery'
								? t('auth.chooseNewPassword')
								: passwordFlow === 'request'
								? t('auth.enterEmailForReset')
								: subtitle}
						</p>

						{passwordFlow === 'recovery' ? (
							<form onSubmit={submitNewPassword} style={{ display: 'grid', gap: '10px' }}>
								<AnimatedInput
									type="password"
									value={passwordResetForm.password}
									onChange={(event) => setPasswordResetForm((c) => ({ ...c, password: event.target.value }))}
									placeholder={t('auth.newPasswordPlaceholder')}
									autoComplete="new-password"
									baseStyle={inputBase}
									focusStyle={inputFocus}
								/>
								<AnimatedInput
									type="password"
									value={passwordResetForm.confirmPassword}
									onChange={(event) => setPasswordResetForm((c) => ({ ...c, confirmPassword: event.target.value }))}
									placeholder={t('auth.confirmPasswordPlaceholder')}
									autoComplete="new-password"
									baseStyle={inputBase}
									focusStyle={inputFocus}
								/>
								<AnimatedButton
									type="submit"
									disabled={isSubmitting}
									baseStyle={{ ...primaryBtn, opacity: isSubmitting ? 0.75 : 1 }}
									hoverStyle={primaryBtnHover}
									activeStyle={primaryBtnActive}
								>
									{isSubmitting ? <><Spinner />{t('auth.updating')}</> : t('auth.changePassword')}
								</AnimatedButton>
								<AnimatedButton
									type="button"
									onClick={() => setPasswordFlow('none')}
									disabled={isSubmitting}
									baseStyle={{ ...ghostBtn, opacity: isSubmitting ? 0.55 : 1 }}
									hoverStyle={ghostBtnHover}
								>
									{t('auth.back')}
								</AnimatedButton>
							</form>

						) : passwordFlow === 'request' ? (
							<form onSubmit={submitPasswordResetRequest} style={{ display: 'grid', gap: '10px' }}>
								<AnimatedInput
									type="email"
									value={passwordResetEmail}
									onChange={(event) => setPasswordResetEmail(event.target.value)}
									placeholder="your@email.com"
									autoComplete="email"
									baseStyle={inputBase}
									focusStyle={inputFocus}
								/>
								<AnimatedButton
									type="submit"
									disabled={isSubmitting}
									baseStyle={{ ...primaryBtn, opacity: isSubmitting ? 0.75 : 1 }}
									hoverStyle={primaryBtnHover}
									activeStyle={primaryBtnActive}
								>
									{isSubmitting ? <><Spinner />{t('auth.sending')}</> : t('auth.sendLink')}
								</AnimatedButton>
								<AnimatedButton
									type="button"
									onClick={() => { setPasswordFlow('none'); setPasswordResetSent(false); setFormError(''); }}
									disabled={isSubmitting}
									baseStyle={{ ...ghostBtn, opacity: isSubmitting ? 0.55 : 1 }}
									hoverStyle={ghostBtnHover}
								>
									{t('auth.back')}
								</AnimatedButton>
								{passwordResetSent && (
									<div style={{
										padding: '11px 14px',
										borderRadius: '10px',
										background: 'rgba(134,239,172,0.1)',
										border: '1px solid rgba(134,239,172,0.25)',
										color: successTextColor,
										fontSize: '13.5px',
									}}>
										{t('auth.linkSent')}
									</div>
								)}
							</form>

						) : (
							<>
								{/* Google */}
								<AnimatedButton
									onClick={signInWithGoogle}
									disabled={isSubmitting}
									baseStyle={{
										width: '100%',
										border: `1.5px solid ${hexToRgba(textSecondary, 0.22)}`,
										background: hexToRgba(bgPrimary, 0.5),
										color: textPrimary,
										borderRadius: '12px',
										padding: '13px 16px',
										cursor: isSubmitting ? 'not-allowed' : 'pointer',
										opacity: isSubmitting ? 0.65 : 1,
										fontWeight: 600,
										fontSize: '15px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										gap: '10px',
										transition: 'background 0.22s ease, border-color 0.22s ease, transform 0.2s ease, box-shadow 0.2s ease',
									}}
									hoverStyle={isSubmitting ? {} : {
										background: hexToRgba(bgPrimary, 0.75),
										borderColor: hexToRgba(activeAccent, 0.4),
										transform: 'translateY(-1px)',
										boxShadow: `0 6px 20px ${hexToRgba(activeAccent, 0.12)}`,
									}}
									activeStyle={{ transform: 'translateY(0px) scale(0.985)' }}
								>
									<span style={{
										display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
										width: 22, height: 22, borderRadius: '50%', background: '#fff', flexShrink: 0,
									}}>
										<FcGoogle size={14} />
									</span>
									<span>{t('auth.continueWithGoogle')}</span>
								</AnimatedButton>

								{/* divider */}
								<div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '16px 0 14px' }}>
									<div style={{ height: 1, flex: 1, background: hexToRgba(textSecondary, 0.18) }} />
									<span style={{ color: textSecondary, fontSize: '11px', fontWeight: 700, letterSpacing: '1px' }}>{t('auth.or')}</span>
									<div style={{ height: 1, flex: 1, background: hexToRgba(textSecondary, 0.18) }} />
								</div>

								{/* tab toggle */}
								<div style={{
									display: 'flex',
									gap: '6px',
									marginBottom: '16px',
									background: hexToRgba(bgPrimary, 0.55),
									borderRadius: '13px',
									padding: '4px',
									border: `1px solid ${hexToRgba(textSecondary, 0.12)}`,
								}}>
									{[['login', t('auth.signInTab')], ['signup', t('auth.signUpTab')]].map(([val, label]) => (
										<AnimatedButton
											key={val}
											onClick={() => { setMode(val); setFormError(''); }}
											disabled={isSubmitting}
											baseStyle={{
												flex: 1,
												border: 'none',
												background: mode === val
													? `linear-gradient(135deg, ${activeAccent} 0%, #a855f7 100%)`
													: 'transparent',
												color: mode === val ? '#ffffff' : textSecondary,
												borderRadius: '10px',
												padding: '9px 12px',
												cursor: isSubmitting ? 'not-allowed' : 'pointer',
												fontWeight: mode === val ? 700 : 560,
												fontSize: '14px',
												transition: 'background 0.22s ease, color 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease, transform 0.22s ease',
												boxShadow: mode === val
													? `0 6px 16px ${hexToRgba(activeAccent, 0.26)}, inset 0 1px 0 ${hexToRgba('#FFFFFF', 0.14)}`
													: 'none',
											}}
											hoverStyle={mode !== val && !isSubmitting ? { color: textPrimary, background: hexToRgba(activeAccent, 0.08) } : {}}
										>
											{label}
										</AnimatedButton>
									))}
								</div>

								{/* login form */}
								{mode === 'login' ? (
									<form onSubmit={submitLogin} style={{ display: 'grid', gap: '12px' }}>
										<AnimatedInput
											type="email"
											value={loginForm.email}
											onChange={(e) => onChangeLoginField('email', e.target.value)}
											placeholder="your@email.com"
											autoComplete="email"
											baseStyle={inputBase}
											focusStyle={inputFocus}
										/>
										<AnimatedInput
											type={showLoginPassword ? 'text' : 'password'}
											value={loginForm.password}
											onChange={(e) => onChangeLoginField('password', e.target.value)}
											placeholder={t('auth.passwordPlaceholder')}
											autoComplete="current-password"
											baseStyle={inputBase}
											focusStyle={inputFocus}
											rightSlot={
												<button
													type="button"
													onClick={() => setShowLoginPassword(!showLoginPassword)}
													style={eyeBtn}
												>
													{showLoginPassword ? <FiEye size={17} /> : <FiEyeOff size={17} />}
												</button>
											}
										/>
										<AnimatedButton
											type="submit"
											disabled={isSubmitting}
											baseStyle={{ ...primaryBtn, opacity: isSubmitting ? 0.75 : 1 }}
											hoverStyle={primaryBtnHover}
											activeStyle={primaryBtnActive}
										>
											{isSubmitting ? <><Spinner />{t('auth.signingIn')}</> : t('auth.signIn')}
										</AnimatedButton>
										<AnimatedButton
											type="button"
											onClick={() => { setPasswordFlow('request'); setPasswordResetEmail(loginForm.email || ''); setPasswordResetSent(false); setFormError(''); }}
											disabled={isSubmitting}
											baseStyle={{
												border: 'none',
												background: 'transparent',
												color: activeAccent,
												padding: '2px 0',
												cursor: 'pointer',
												justifySelf: 'start',
												fontSize: '13.5px',
												fontWeight: 500,
												opacity: isSubmitting ? 0.5 : 1,
												transition: 'opacity 0.18s ease',
												textDecoration: 'underline',
												textDecorationColor: hexToRgba(activeAccent, 0.4),
												textUnderlineOffset: '3px',
											}}
											hoverStyle={{ opacity: 0.8 }}
										>
											{t('auth.forgotPassword')}
										</AnimatedButton>
									</form>

								) : (
									/* signup form */
									<form onSubmit={submitSignup} style={{ display: 'grid', gap: '10px' }}>
										<AnimatedInput
											type="text"
											value={signupForm.name}
											onChange={(e) => onChangeSignupField('name', e.target.value)}
											placeholder={t('auth.fullNamePlaceholder')}
											autoComplete="name"
											baseStyle={inputBase}
											focusStyle={inputFocus}
										/>
										<AnimatedInput
											type="text"
											value={signupForm.username}
											onChange={(e) => onChangeSignupField('username', e.target.value)}
											placeholder={t('auth.usernamePlaceholder')}
											autoComplete="username"
											baseStyle={inputBase}
											focusStyle={inputFocus}
										/>
										<AnimatedInput
											type="email"
											value={signupForm.email}
											onChange={(e) => onChangeSignupField('email', e.target.value)}
											placeholder="your@email.com"
											autoComplete="email"
											baseStyle={inputBase}
											focusStyle={inputFocus}
										/>
										<AnimatedInput
											type={showSignupPassword ? 'text' : 'password'}
											value={signupForm.password}
											onChange={(e) => onChangeSignupField('password', e.target.value)}
											placeholder={t('auth.passwordMinPlaceholder')}
											autoComplete="new-password"
											baseStyle={inputBase}
											focusStyle={inputFocus}
											rightSlot={
												<button
													type="button"
													onClick={() => setShowSignupPassword(!showSignupPassword)}
													style={eyeBtn}
												>
													{showSignupPassword ? <FiEye size={17} /> : <FiEyeOff size={17} />}
												</button>
											}
										/>
										<AnimatedInput
											type={showSignupConfirmPassword ? 'text' : 'password'}
											value={signupForm.confirmPassword}
											onChange={(e) => onChangeSignupField('confirmPassword', e.target.value)}
											placeholder={t('auth.confirmPasswordPlaceholder')}
											autoComplete="new-password"
											baseStyle={inputBase}
											focusStyle={inputFocus}
											rightSlot={
												<button
													type="button"
													onClick={() => setShowSignupConfirmPassword(!showSignupConfirmPassword)}
													style={eyeBtn}
												>
													{showSignupConfirmPassword ? <FiEye size={17} /> : <FiEyeOff size={17} />}
												</button>
											}
										/>
										<AnimatedButton
											type="submit"
											disabled={isSubmitting}
											baseStyle={{ ...primaryBtn, opacity: isSubmitting ? 0.75 : 1 }}
											hoverStyle={primaryBtnHover}
											activeStyle={primaryBtnActive}
										>
											{isSubmitting ? <><Spinner />{t('auth.creating')}</> : t('auth.createMyAccount')}
										</AnimatedButton>
									</form>
								)}
							</>
						)}

						{formError && (
							<div style={{
								marginTop: '16px',
								padding: '11px 14px',
								borderRadius: '10px',
								background: 'rgba(248,113,113,0.09)',
								border: '1px solid rgba(248,113,113,0.22)',
								color: '#F87171',
								fontSize: '13.5px',
								lineHeight: 1.45,
							}}>
								{formError}
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
}
